import React, { useEffect, useState } from 'react'
import {Clock, DollarSign, User, Calendar} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { AgentProfileModal } from './AgentProfileModal'

interface Task {
  _id: string
  title: string
  description: string
  category: string
  budget: number
  currency?: string
  deadline: string
  status: string
  taskType: string
  requesterId: string
  postedBy?: string
  createdAt: string
}

interface Agent {
  _id: string
  name?: string
  username?: string
  profilePicture?: string
}

interface TaskListProps {
  onTaskClick?: (task: Task) => void
}

export function TaskList({ onTaskClick }: TaskListProps) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [selectedAgent, setSelectedAgent] = useState<any>(null)
  const [showAgentProfile, setShowAgentProfile] = useState(false)

  useEffect(() => {
    fetchTasks()
  }, [filter])

  const fetchTasks = async () => {
    setLoading(true)
    try {
      const filterObj = filter === 'all' ? {} : { status: filter }
      const [tasksResult, agentsResult] = await Promise.all([
        lumi.entities.tasks.list({
          filter: filterObj,
          sort: { createdAt: -1 },
          limit: 20
        }),
        lumi.entities.agents.list({
          filter: {},
          limit: 100
        })
      ])
      
      // Filter out deleted tasks
      const activeTasks = tasksResult.list.filter(task => task.status !== 'deleted')
      setTasks(activeTasks)
      
      // Filter out deleted agents
      const activeAgents = agentsResult.list.filter(agent => 
        !agent.name?.includes('[DELETED]')
      )
      setAgents(activeAgents)
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      open: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      created: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      claimed: 'bg-purple-300/20 text-purple-300 border-purple-300/50',
      in_progress: 'bg-purple-600/20 text-purple-500 border-purple-600/50',
      completed: 'bg-purple-400/20 text-purple-400 border-purple-400/50',
      disputed: 'bg-red-500/20 text-red-400 border-red-500/50'
    }
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/50'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatBudget = (budget: number, currency?: string) => {
    const formattedNumber = budget.toLocaleString('en-US')
    const currencySymbol = currency || 'XYBER'
    return `${formattedNumber} ${currencySymbol}`
  }

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 min-h-screen">
      <div className="flex gap-3 flex-wrap">
        {['all', 'open', 'created', 'claimed', 'in_progress', 'completed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === status
                ? 'bg-gradient-to-r from-purple-500 to-purple-700 text-white'
                : 'bg-gray-900 text-gray-300 hover:bg-gray-800 border border-gray-700'
            }`}
          >
            {status === 'all' ? 'All Tasks' : status.replace('_', ' ').toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid gap-6">
        {tasks.map((task) => (
          <div
            key={task._id}
            className="bg-black/50 border border-gray-800 rounded-xl p-6 hover:border-purple-500/50 transition-all backdrop-blur-sm"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-bold text-white">{task.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(task.status)}`}>
                    {task.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-400">Posted by:</span>
                  {(() => {
                    const agentId = task.postedBy || task.requesterId
                    const agent = agents.find(a => a._id === agentId)
                    const displayName = (task as any).postedByName || agent?.name || agentId?.slice(0, 8) || 'Unknown'
                    
                    return (
                      <button
                        onClick={() => {
                          if (!agentId || agentId === 'undefined') {
                            console.warn('Invalid agent ID:', agentId)
                            return
                          }
                          const foundAgent = agents.find(a => a._id === agentId)
                          if (foundAgent) {
                            setSelectedAgent(foundAgent)
                            setShowAgentProfile(true)
                          }
                        }}
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                      >
                        {agent?.profilePicture && (
                          <img 
                            src={agent.profilePicture} 
                            alt="Profile" 
                            className="w-6 h-6 rounded-full object-cover border border-purple-500"
                          />
                        )}
                        <span className="text-purple-400 font-medium hover:underline">
                          {displayName}
                        </span>
                      </button>
                    )
                  })()}
                </div>
                <p className="text-slate-400 line-clamp-2">{task.description}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-2 text-slate-400">
                <span className="px-3 py-1 bg-gray-900 rounded-full text-sm text-purple-400">
                  {task.category}
                </span>
              </div>
              {task.taskType && (
                <div className="flex items-center gap-2 text-slate-400">
                  <span className="px-3 py-1 bg-gray-900 rounded-full text-sm text-purple-300">
                    {task.taskType.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-slate-400">
                  <DollarSign className="w-4 h-4" />
                  <span className="font-semibold text-white">{formatBudget(task.budget, task.currency)}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Due: {formatDate(task.deadline)}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Posted: {formatDate(task.createdAt)}</span>
                </div>
              </div>

              <button 
                onClick={() => onTaskClick?.(task)}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all"
              >
                View Details
              </button>
            </div>
          </div>
        ))}

        {tasks.length === 0 && (
          <div className="text-center py-20">
            <p className="text-slate-400 text-lg">No tasks found</p>
          </div>
        )}
      </div>

      {/* Agent Profile Modal */}
      <AgentProfileModal
        agent={selectedAgent}
        isOpen={showAgentProfile}
        onClose={() => {
          setShowAgentProfile(false)
          setSelectedAgent(null)
        }}
      />
    </div>
  )
}
